import React, { useState, useMemo } from 'react';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';
import Card from '../../../ui/Card';
import { ICONS } from '../../../ui/icon-map';
import Badge from '../../../ui/Badge';
import Stat, { StatGrid } from '../../../ui/Stat/Stat';
import EmptyState from '../../../ui/EmptyState';
import DrillDownModal from '../components/DrillDownModal';
import ShareRow from '../components/ShareRow';
import { openedIn, closedIn } from '../kpi.logic';
import { normalizePlate } from '../../../services/possessionMatcher';
import { formatContractDateTime } from '../../../utils/contractDateUtils';

const utilizationColor = (pct) => (
    pct >= 80 ? 'var(--success)' :
    pct >= 40 ? 'var(--warning)' : 'var(--danger)'
);

const FleetKPIs = ({ data, allOps = [], filters }) => {
    const { hasPermission } = usePermissions();
    // Result unused (drill-down is not gated here); the call is kept for its missing-permissions log warning.
    hasPermission(PERMISSIONS.view_kpi_drilldown);
    const [modal, setModal] = useState({ open: false, title: '', data: [] });

    const stats = useMemo(() => {

        // Helper for Branch Filtering (Handles "Airport" Combo)
        const isBranchMatch = (b) => {
            const branch = (b || '').trim();
            if (filters.branch === 'All') return true;
            if (filters.branch === 'Airport') return branch === 'Terminal 1' || branch === 'Terminal 2';
            return branch === filters.branch;
        };

        // 0. Pre-Process Active Operations for Branch Inference
        const activeOpsMap = new Map();
        allOps.forEach(op => {
            if (op.operationStatus === 'In-Progress' && op.plateNo) {
                const normPlate = normalizePlate(op.plateNo);
                activeOpsMap.set(normPlate, op.branch);
            }
        });

        // 0. Deduplicate Fleet Data (Safety Check) & Enrich Branch
        const uniqueDataMap = new Map();
        data.forEach(f => {
            const normPlate = normalizePlate(f.plateNo);
            if (normPlate && !uniqueDataMap.has(normPlate)) {
                // ENRICHMENT: If car is active, use contract branch instead of fleet branch
                const activeBranch = activeOpsMap.get(normPlate);
                const effectiveBranch = activeBranch || f.branch;

                // Only add if it matches the branch filter (Optimization)
                // Actually, we must add all to map first, then filter, or just map here.
                // Better to map all valid cars first.
                uniqueDataMap.set(normPlate, { ...f, branch: effectiveBranch });
            }
        });

        const uniqueData = Array.from(uniqueDataMap.values());

        // 1. Basic Counts (Now using effective branch)
        // Filter uniqueData by branch
        const branchFleet = uniqueData.filter(f => isBranchMatch(f.branch));
        const total = branchFleet.length;

        // Set of valid plates for this branch (for strict consistency)
        const branchFleetPlates = new Set(branchFleet.map(f => normalizePlate(f.plateNo)));

        // 2. Contracts (Operations) Analysis
        // 2. Contracts (Operations) Analysis
        // Filter valid ops for counts
        const period = { from: filters.startDate, to: filters.endDate, branch: filters.branch };
        const openedList = openedIn(allOps, period);
        const closedList = closedIn(allOps, period);
        const opened = openedList.length;
        const closed = closedList.length;

        // STRICT CONSISTENCY: Only count active contracts if the car is in the branch fleet
        const activeOpsList = allOps.filter(op => {
            // Is it an active contract?
            if (op.operationStatus !== 'In-Progress') return false;

            // Does it belong to this branch? (Checking op.branch directly is usually enough due to enrichment,
            // but strict check ensures we don't count cars that don't exist in fleet)
            const normPlate = normalizePlate(op.plateNo);
            return branchFleetPlates.has(normPlate);
        });
        const currentlyOnRent = activeOpsList.length;



        // 3. In maintenance = the car's status now (Ejar, via the fleet): "Maintenance" / "Preventive Maintenance".
        // Not the maintenance log: a log whose return (Date IN) was never written stays "open" for months
        // while the car is rented or ready again. At T1 / T2 that status = a car held for a booking, never the
        // workshop; at Business Bay / Invygo it is real maintenance (owner, 29 Oct 2026 - engine api/overview.py).
        const maintenanceList = branchFleet.filter(f => /maint/i.test(String(f.fleetStatus || ''))
            && !/airport|terminal/i.test(String(f.branch || '')));
        const inMaintenance = maintenanceList.length;

        // 4. Ready Units (Current Status)
        const readyUnits = branchFleet.filter(f => {
            return ['available', 'ready'].includes(f.fleetStatus.toLowerCase());
        }).length;

        const utilization = total > 0 ? Math.round((currentlyOnRent / total) * 100) : 0;

        const byCategory = {};
        branchFleet.forEach(f => {
            const cat = f.carCategory || 'Standard';
            byCategory[cat] = (byCategory[cat] || 0) + 1;
        });

        // 5. Top Models (by Frequency in Fleet)
        const modelCounts = {};
        allOps.forEach(op => {
            const normPlate = normalizePlate(op.plateNo);
            if (op.operationStatus === 'In-Progress' && branchFleetPlates.has(normPlate)) {
                // strict normalization to avoid "DZIRE" vs "Dzire " duplicates
                let model = (op.model || 'Unknown').toUpperCase().trim().replace(/\s+/g, ' ');
                if (model.includes('DZIRE')) model = 'DZIRE';
                if (model.includes('SUNNY')) model = 'SUNNY';
                if (model.includes('PEGAS')) model = 'PEGAS';
                if (model.includes('ATTRAGE')) model = 'ATTRAGE';

                modelCounts[model] = (modelCounts[model] || 0) + 1;
            }
        });

        let sortedModels = Object.entries(modelCounts).sort((a, b) => b[1] - a[1]);

        // Take top 5
        const top5 = sortedModels.slice(0, 5).map(([name, count]) => ({ name, count }));
        const top5Names = top5.map(m => m.name);

        // Calculate Others
        const othersCount = sortedModels.slice(5).reduce((sum, [, count]) => sum + count, 0);

        const topModels = [...top5];
        if (othersCount > 0) {
            topModels.push({ name: 'OTHERS', count: othersCount });
        }

        // 7. Branch Distribution (Fleet Based)
        const byBranch = {};
        branchFleet.forEach(f => {
            const b = f.branch || 'Unassigned';
            byBranch[b] = (byBranch[b] || 0) + 1;
        });

        // 8. Contract Type Mix (active operations)
        const byContractType = {};
        allOps.forEach(op => {
            const normPlate = normalizePlate(op.plateNo);
            if (op.operationStatus === 'In-Progress' && branchFleetPlates.has(normPlate)) {
                const t = op.type || 'Daily';
                byContractType[t] = (byContractType[t] || 0) + 1;
            }
        });

        // 9. Top Active Customers
        const customerCounts = {};
        allOps.forEach(op => {
            const normPlate = normalizePlate(op.plateNo);
            if (op.operationStatus === 'In-Progress' && branchFleetPlates.has(normPlate)) {
                const c = (op.customer || 'Unknown').trim();
                customerCounts[c] = (customerCounts[c] || 0) + 1;
            }
        });
        const topCustomers = Object.entries(customerCounts)
            .sort((a,b) => b[1] - a[1])
            .slice(0, 5)
            .map(([name, count]) => ({ name, count }));

        return {
            total,
            opened,
            closed,
            currentlyOnRent,
            inMaintenance,
            readyUnits,
            utilization,
            byCategory: Object.entries(byCategory).sort((a,b) => b[1] - a[1]),
            topModels,
            top5Names,
            byBranch: Object.entries(byBranch).sort((a,b) => b[1] - a[1]),
            byContractType: Object.entries(byContractType).sort((a,b) => b[1] - a[1]),
            topCustomers,
            isBranchMatch,
            openedList,
            closedList,
            maintenanceList,
            branchFleetPlates, // Export for DrillDown safety
            normalizePlate
        };
    }, [data, allOps, filters]);

    return (
        <div className="flex flex-col gap-6">

            <StatGrid min={160}>
                <Stat
                    label="Total Fleet"
                    icon={ICONS.fleet}
                    value={stats.total}
                    onClick={() => setModal({ open: true, title: 'Total Fleet', data: data.filter(f => stats.isBranchMatch(f.branch)) })}
                />
                <Stat
                    label="Contracts Opened"
                    icon={ICONS.contracts}
                    value={stats.opened}
                    onClick={() => setModal({ open: true, title: 'Contracts Opened', data: stats.openedList })}
                />
                <Stat
                    label="Contracts Closed"
                    icon={ICONS.check}
                    value={stats.closed}
                    onClick={() => setModal({ open: true, title: 'Contracts Closed', data: stats.closedList })}
                />
                <Stat
                    label="Currently Out"
                    icon={ICONS.car}
                    value={stats.currentlyOnRent}
                    onClick={() => setModal({ open: true, title: 'Currently On Rent', data: allOps.filter(op => {
                        return op.operationStatus === 'In-Progress' && stats.branchFleetPlates.has(stats.normalizePlate(op.plateNo));
                    }) })}
                />
                <Stat
                    label="In Maintenance"
                    icon={ICONS.maintenance}
                    value={stats.inMaintenance}
                    tone={stats.inMaintenance > 0 ? 'warning' : undefined}
                    meta="Status Maintenance now · Business Bay / Invygo"
                    onClick={() => setModal({ open: true, title: 'Units In Maintenance', data: stats.maintenanceList })}
                />
                <Stat
                    label="Ready for Rent"
                    icon={ICONS.success}
                    value={stats.readyUnits}
                    onClick={() => setModal({ open: true, title: 'Units Ready for Rent', data: data.filter(f => stats.isBranchMatch(f.branch) && ['available', 'ready'].includes(f.fleetStatus.toLowerCase())) })}
                />
                <Stat label="Utilization %" icon={ICONS.chart} value={`${stats.utilization}%`} />
            </StatGrid>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* 1. Real-Time Utilization */}
                <Card title="Real-Time Utilization" subtitle="Active contracts by model. Select a model to see its contracts.">
                    <div className="flex flex-col items-center justify-center min-h-[300px] overflow-hidden">
                        <div className="relative w-[280px] h-[280px] max-w-full flex items-center justify-center">
                            {/* Centre (Utilization) */}
                            <div className="w-36 h-36 rounded-full bg-white flex flex-col items-center justify-center z-10 relative border-8 border-gray-100">
                                <span className="text-3xl font-semibold text-gray-900 tabular-nums">{stats.utilization}%</span>
                                <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mt-1">On rent</span>

                                {/* SVG Ring */}
                                <svg className="absolute top-[-8px] left-[-8px] w-[144px] h-[144px] -rotate-90 pointer-events-none" aria-hidden="true">
                                    <circle
                                        cx="72" cy="72" r="66"
                                        fill="transparent"
                                        stroke={utilizationColor(stats.utilization)}
                                        strokeWidth="8"
                                        strokeDasharray={`${(stats.utilization / 100) * 415}, 415`}
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </div>

                            {/* Satellites (Top Models) */}
                            {stats.topModels.map((model, i) => {
                                const totalSatellites = stats.topModels.length;
                                const angle = (i * (360 / totalSatellites)) - 90; // Start from top
                                const radius = 115; // Distance from center
                                const x = radius * Math.cos(angle * (Math.PI / 180));
                                const y = radius * Math.sin(angle * (Math.PI / 180));

                                return (
                                    <button
                                        type="button"
                                        key={model.name}
                                        className="absolute flex flex-col items-center justify-center w-16 h-16 bg-white rounded-full border border-gray-200 hover:border-purple-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 z-0"
                                        style={{
                                            transform: `translate(${x}px, ${y}px)`,
                                        }}
                                        title={`${model.name}: ${model.count} Units`}
                                        aria-label={`${model.name}: ${model.count} units on rent`}
                                        onClick={() => {
                                            const isOthers = model.name === 'OTHERS';
                                            const filtered = allOps.filter(op => {
                                                const branchMatch = stats.branchFleetPlates.has(stats.normalizePlate(op.plateNo));
                                                const isActive = op.operationStatus === 'In-Progress';

                                                if (!branchMatch || !isActive) return false;

                                                let opModel = (op.model || 'Unknown').toUpperCase().trim().replace(/\s+/g, ' ');
                                                if (opModel.includes('DZIRE')) opModel = 'DZIRE';
                                                if (opModel.includes('SUNNY')) opModel = 'SUNNY';
                                                if (opModel.includes('PEGAS')) opModel = 'PEGAS';
                                                if (opModel.includes('ATTRAGE')) opModel = 'ATTRAGE';

                                                if (isOthers) {
                                                    return !stats.top5Names.includes(opModel);
                                                }
                                                return opModel === model.name;
                                            });

                                            // Format data for display
                                            const displayData = filtered.map(op => ({
                                                'Contract No': op.contractNo || '-',
                                                'Model': op.model || '-',
                                                'Plate No': op.plateNo || '-',
                                                'Customer': op.customer || '-',
                                                'Branch': op.branch || '-',
                                                'Status': op.operationStatus || '-',
                                                // dd/mm/yyyy HH:MM (the old airport AM/PM text read month first)
                                                'Pickup Date': formatContractDateTime(op.pickupDate) || op.pickupDate || '-',
                                                'Days Open': op.pickupAt ? Math.ceil((Date.now() - op.pickupAt) / (1000 * 60 * 60 * 24)) : '-'
                                            }));

                                            setModal({
                                                open: true,
                                                title: isOthers ? 'Other Models on Rent' : `${model.name} on Rent`,
                                                data: displayData
                                            });
                                        }}
                                    >
                                        <span className="text-base font-semibold text-gray-900 tabular-nums">{model.count}</span>
                                        <span className="text-[11px] font-medium text-gray-600 uppercase text-center leading-tight px-1 overflow-hidden whitespace-nowrap text-ellipsis max-w-full">
                                            {model.name.split(' ')[0]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mt-4 flex justify-around border-t border-gray-100 pt-4">
                        <div className="flex flex-col items-center">
                            <span className="text-xs font-medium text-gray-500 mb-1">Available</span>
                            <span className="text-xl font-semibold text-gray-900 tabular-nums">{stats.readyUnits}</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="text-xs font-medium text-gray-500 mb-1">Occupied</span>
                            <span className="text-xl font-semibold text-gray-900 tabular-nums">{stats.currentlyOnRent}</span>
                        </div>
                    </div>
                </Card>

                {/* 2. Units by Category */}
                <Card
                    title="Units by Category"
                    actions={<Badge variant="accent">{stats.total} total</Badge>}
                >
                    <div className="space-y-2 pr-1 max-h-[350px] overflow-y-auto">
                        <DistributionList
                            items={stats.byCategory}
                            total={stats.total}
                            onItemClick={(cat) => setModal({
                                open: true,
                                title: `Fleet Units - ${cat}`,
                                data: data.filter(f => stats.isBranchMatch(f.branch) && f.carCategory === cat)
                            })}
                        />
                    </div>
                </Card>

            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* 4. Contract Type Mix */}
                <Card title="Contract Type Mix">
                    <div className="space-y-2">
                        {(stats.byContractType || []).length === 0 && (
                            <EmptyState icon={ICONS.contracts} title="No active contracts" />
                        )}
                        {(stats.byContractType || []).map(([type, count]) => {
                            const numCount = Number(count) || 0;
                            const percentage = stats.currentlyOnRent > 0 ? Math.round((numCount / stats.currentlyOnRent) * 100) : 0;

                            return (
                                <ShareRow
                                    key={type}
                                    label={type}
                                    sub={`${numCount} contracts`}
                                    percent={percentage}
                                    barWidth={percentage}
                                    onClick={() => setModal({
                                        open: true,
                                        title: `Contracts - ${type}`,
                                        data: allOps.filter(op => op.operationStatus === 'In-Progress' && stats.branchFleetPlates.has(stats.normalizePlate(op.plateNo)) && op.type === type)
                                    })}
                                />
                            );
                        })}
                    </div>
                </Card>

                {/* 5. Top Active Customers */}
                <Card title="Top Active Customers" subtitle="Customers with the most active contracts">
                    <div className="space-y-2">
                        {(() => {
                            const customers = stats.topCustomers || [];
                            if (customers.length === 0) {
                                return <EmptyState icon={ICONS.users} title="No active customers" />;
                            }
                            const maxVal = Math.max(1, ...customers.map(c => Number(c.count) || 0));

                            return customers.map((c, i) => {
                                const numCount = Number(c.count) || 0;
                                const widthPercentage = (numCount / maxVal) * 100;

                                return (
                                    <ShareRow
                                        key={c.name}
                                        rank={i + 1}
                                        label={c.name}
                                        value={numCount}
                                        barWidth={widthPercentage}
                                        onClick={() => setModal({
                                            open: true,
                                            title: `Active Contracts - ${c.name}`,
                                            data: allOps.filter(op => op.operationStatus === 'In-Progress' && stats.branchFleetPlates.has(stats.normalizePlate(op.plateNo)) && (op.customer || 'Unknown').trim() === c.name)
                                        })}
                                    />
                                );
                            });
                        })()}
                    </div>
                </Card>

            </div>

            <DrillDownModal
                isOpen={modal.open}
                onClose={() => setModal({ ...modal, open: false })}
                title={modal.title}
                data={modal.data}
            />
        </div>
    );
};

// Internal Helper for Consistent List Styling
const DistributionList = ({ items, total, onItemClick }) => {
    if (!items || items.length === 0) {
        return <EmptyState icon={ICONS.fleet} title="No data available" />;
    }

    return items.map(([label, count]) => {
        const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
        // Width matches percentage exactly (relative to total)
        return (
            <ShareRow
                key={label}
                label={label}
                value={`(${count})`}
                percent={percentage}
                barWidth={percentage}
                onClick={() => onItemClick(label)}
            />
        );
    });
};

export default FleetKPIs;
